import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { isWorking } from '../utils/content';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const REFRESH_MS = 8000;

export const isMissingContent = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205'].includes(error?.code) || /content_items|content_settings|content_credits|request_content|discard_content/i.test(error?.message || '');

const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre este negocio (solo el dueño o un administrador).';
  if (error?.code === '22023' && error?.message) return error.message.charAt(0).toUpperCase() + error.message.slice(1) + (/[.)]$/.test(error.message) ? '' : '.');
  return fallback;
};

// Textos pedidos a la IA: se piden por la base y n8n los redacta (cada minuto). Aquí se piden, se miran, se copian o se descartan.
export const useContent = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [items, setItems] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imageUrls, setImageUrls] = useState({});

  const load = useCallback(async () => {
    if (!activeBusinessId || isReadOnlyDemo) { setLoading(false); return; }
    const business = String(activeBusinessId);
    const [list, cr] = await Promise.all([
      supabase.from('content_items').select('id, kind, subject, brief, status, result, image_path, error, created_at').eq('business_id', business).order('created_at', { ascending: false }).limit(40),
      supabase.rpc('content_credits', { p_business: business })
    ]);
    const error = list.error || cr.error;
    if (error) {
      const gone = isMissingContent(error);
      setMissing(gone);
      if (!gone) toast.error(explain(error, 'No se pudo leer el contenido.'));
      setItems([]);
    } else {
      setMissing(false);
      setItems(list.data || []);
      setCredits(cr.data || null);
      // Las imágenes están en un bucket privado: se muestran con enlaces firmados de 1 hora.
      const withImage = (list.data || []).filter((i) => i.kind === 'imagen' && i.status === 'listo' && i.image_path);
      if (withImage.length) {
        const signed = await supabase.storage.from('contenido').createSignedUrls(withImage.map((i) => i.image_path), 3600);
        const byPath = new Map((signed.data || []).filter((s) => s.signedUrl).map((s) => [s.path, s.signedUrl]));
        setImageUrls(Object.fromEntries(withImage.filter((i) => byPath.has(i.image_path)).map((i) => [i.id, byPath.get(i.image_path)])));
      } else {
        setImageUrls({});
      }
    }
    setLoading(false);
  }, [activeBusinessId, isReadOnlyDemo]);

  useEffect(() => { load(); }, [load]);
  const working = items.some((i) => isWorking(i.status));
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [working, load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const request = async ({ kinds, subject, brief }) => {
    if (guardDemo()) return false;
    setBusy(true);
    const { data, error } = await supabase.rpc('request_content', { p_business: String(activeBusinessId), p_kinds: kinds, p_subject: String(subject).trim(), p_brief: String(brief || '').trim() || null });
    setBusy(false);
    if (error) {
      toast.error(isMissingContent(error) ? 'Falta ejecutar la migración 20261020a_content_studio.sql en Supabase.' : explain(error, 'No se pudo pedir el contenido.'));
      if (isMissingContent(error)) setMissing(true);
      return false;
    }
    toast.success(`${data?.ids?.length || kinds.length} texto(s) en cola: aparecen aquí en un par de minutos.`);
    await load();
    return true;
  };

  const discard = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('discard_content', { p_id: id });
    if (error) { toast.error(explain(error, 'No se pudo descartar el texto.')); return false; }
    await load();
    return true;
  };

  // Enlace que fuerza la descarga del archivo (vale 1 minuto).
  const downloadImage = async (item) => {
    const { data, error } = await supabase.storage.from('contenido').createSignedUrl(item.image_path, 60, { download: `imagen-${item.id.slice(0, 8)}.png` });
    if (error || !data?.signedUrl) { toast.error('No se pudo preparar la descarga. Inténtalo de nuevo.'); return null; }
    return data.signedUrl;
  };

  return { items, credits, imageUrls, loading, missing, busy, isReadOnlyDemo, reload: load, request, discard, downloadImage };
};
